import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import TrustBar from "@/pages/home/components/TrustBar";
import AIAnswerBlock from "@/pages/home/components/AIAnswerBlock";
import ProcessSection from "@/pages/home/components/ProcessSection";
import WhyChooseUs from "@/pages/home/components/WhyChooseUs";
import ServiceAreas from "@/pages/home/components/ServiceAreas";
import TestimonialsSection from "@/pages/home/components/TestimonialsSection";
import FAQSection from "@/pages/home/components/FAQSection";
import FinalCTA from "@/pages/home/components/FinalCTA";
import MidPageCTA from "@/pages/home/components/MidPageCTA";
import QuoteForm from "@/pages/home/components/QuoteForm";
import { serviceFaqs } from "@/mocks/faqs";
import { Link } from "react-router-dom";

const serviceItems = [
  { icon: "ri-snowflake-line", title: "System Not Cooling", desc: "Complete diagnostic to identify why your unit isn't producing cold air — from refrigerant leaks to compressor failure. We find the root cause fast." },
  { icon: "ri-typhoon-line", title: "Weak or Restricted Airflow", desc: "Assessment and repair of blower motors, clogged filters, duct blockages, and other causes of reduced airflow throughout your property." },
  { icon: "ri-volume-up-line", title: "Strange Noises", desc: "Identification and repair of unusual sounds — rattling, grinding, squealing — that indicate mechanical wear or component failure in your system." },
  { icon: "ri-drop-line", title: "Refrigerant Issues", desc: "Safe leak detection, certified refrigerant handling, and recharge services to restore your system's cooling capacity and EPA compliance." },
  { icon: "ri-alert-line", title: "Full System Failures", desc: "Emergency diagnosis for complete cooling system failures with same-day repair or replacement recommendations to restore your comfort." },
  { icon: "ri-settings-3-line", title: "Electrical & Control Issues", desc: "Troubleshooting and repair of capacitors, contactors, circuit boards, thermostats, and electrical components for reliable operation." },
];

const serviceQA = [
  {
    q: "What is professional AC repair?",
    a: "AC repair involves diagnosing and fixing issues within your air conditioning system — including mechanical failures, airflow problems, refrigerant leaks, and electrical faults. A certified technician inspects the full system to identify root causes and apply targeted, lasting repairs that restore performance.",
  },
  {
    q: "How do I know if my AC needs professional repair?",
    a: "Common signs include warm air from vents, reduced airflow, unusual noises, frequent cycling, high indoor humidity, visible ice on the unit, or unexpectedly high energy bills. If you notice any of these, contact Knight In Shining Armor Heating & Air for a prompt diagnostic.",
  },
];

export default function ServicesPage() {
  return (
    <main className="bg-[#020B14]">
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[620px] flex items-center pt-20 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/2ec451304c6013a762483c4761cca657.jpg"
          alt="Professional AC Repair Dallas TX — Knight In Shining Armor HVAC"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020B14]/94 via-[#020B14]/78 to-[#020B14]/28" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020B14]/40 via-transparent to-[#020B14]/72" />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0EA5E9]/50 via-[#38bdf8]/20 to-transparent" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-[640px]">
            <nav className="flex items-center gap-2 mb-7" aria-label="breadcrumb">
              <Link to="/" className="font-['Inter'] text-white/40 text-xs hover:text-white/65 transition-colors duration-300">Home</Link>
              <i className="ri-arrow-right-s-line text-white/22 text-xs"></i>
              <span className="font-['Inter'] text-[#7DD3FC] text-xs">Services</span>
            </nav>
            <div className="inline-flex items-center gap-2 border border-[#0EA5E9]/35 bg-[#0EA5E9]/8 rounded-full px-4 py-2 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#38bdf8] animate-pulse flex-shrink-0" />
              <span className="font-['Inter'] text-[#7DD3FC] text-xs font-medium tracking-[0.15em] uppercase whitespace-nowrap">
                Dallas Metro HVAC Specialists
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-white text-4xl md:text-5xl lg:text-[3.4rem] font-bold leading-[1.1] mb-6">
              Professional AC Repair<br className="hidden md:block" /> in Dallas, TX
            </h1>
            <p className="font-['Inter'] text-white/65 text-lg md:text-xl leading-[1.78] mb-10">
              Fast diagnostics and precision repairs to restore your cooling system quickly — backed by licensed, certified HVAC technicians who know Dallas heat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#quote"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#0a1f3c] hover:bg-gray-50 font-['Inter'] font-bold text-[0.95rem] px-9 py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_8px_28px_rgba(255,255,255,0.10)] hover:-translate-y-[1.5px]"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-[#1e3a5f]"></i>
                </div>
                Schedule Diagnostic
              </a>
              <a
                href="tel:2148703185"
                className="inline-flex items-center justify-center gap-2 bg-white/6 hover:bg-white/12 border border-white/25 text-white font-['Inter'] font-semibold text-[0.95rem] px-9 py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-[#7DD3FC] text-sm"></i>
                </div>
                Call 214-870-3185
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* What We Handle */}
      <section className="bg-[#050F1A] py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-16">
            <div>
              <p className="font-['Inter'] text-[#7DD3FC] text-xs font-semibold uppercase tracking-[0.18em] mb-4">Our Expertise</p>
              <h2 className="font-['Playfair_Display'] text-white text-4xl md:text-5xl font-bold leading-tight">
                AC Issues We Handle
              </h2>
            </div>
            <div>
              <p className="font-['Inter'] text-white/50 text-base leading-[1.8]">
                From minor performance issues to full system failures — every problem diagnosed and resolved with precision by Dallas-certified technicians.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {serviceItems.map((item, idx) => (
              <div
                key={item.title}
                className="bg-[#071220] border border-white/6 rounded-2xl p-7 hover:border-[#0EA5E9]/30 hover:bg-[#071a2d] transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/15 group-hover:bg-[#0EA5E9]/20 group-hover:border-[#0EA5E9]/35 transition-all duration-300 flex-shrink-0">
                    <i className={`${item.icon} text-[#7DD3FC] text-xl`}></i>
                  </div>
                  <span className="font-['Playfair_Display'] text-white/8 group-hover:text-white/14 text-3xl font-bold leading-none transition-colors duration-300">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-['Inter'] text-white font-bold text-base mb-2.5 leading-snug">{item.title}</h3>
                <p className="font-['Inter'] text-white/50 text-sm leading-[1.78]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MidPageCTA
        headline="Not sure what's wrong with your system?"
        subtext="Our certified technicians will diagnose accurately and give you transparent options — no pressure."
        ctaLabel="Schedule Diagnostic"
      />

      <AIAnswerBlock items={serviceQA} title="Understanding AC Repair" />
      <ProcessSection subtitle="Our systematic process ensures your AC is diagnosed accurately and repaired right the first time." />
      <WhyChooseUs />
      <ServiceAreas />
      <TestimonialsSection />
      <QuoteForm />
      <FAQSection faqs={serviceFaqs} title="AC Repair FAQs" />
      <FinalCTA
        headline="Ready to Restore Your Cooling?"
        subheadline="Don't suffer through Texas heat. Our technicians are ready to diagnose and repair your AC system — same-day service available."
        ctaLabel="Schedule AC Repair"
      />
      <Footer />
    </main>
  );
}
